/**
 * Effective air changes per hour (eACH) and source emission strength, from an
 * exponential fit of a concentration time series.
 *
 * THE MODEL. One well-mixed zone, one pollutant, constant source and constant
 * outdoor concentration over the fitting window, so that
 * `dC/dt = S/V - lambda * (C - C_out)`. Its solution approaches a steady state
 * exponentially: `C(t) = C_inf + (C_0 - C_inf) * exp(-lambda * t)`, where
 * `C_inf = C_out + S / (lambda * V)`.
 *
 * Fitting that curve to five minutes of readings therefore gives both numbers
 * the request asks for.
 * The eACH is lambda itself, in 1/h. The source strength is
 * lambda * (C_inf - C_out), in concentration units per hour per unit volume,
 * multiplied by the room volume for an absolute rate.
 *
 * It works equally well on a DECAY (source off, concentration falling to the
 * outdoor level) and on a BUILD-UP (source on, concentration rising to a new
 * plateau) - the sign of the amplitude tells them apart, lambda is positive
 * either way.
 *
 * "EFFECTIVE" MATTERS. For CO2, lambda really is the air change rate: CO2 is
 * inert and does not settle. For PN0.3 and PM2.5 the same lambda also contains
 * deposition onto surfaces and any filtration, so eACH_PN03 and eACH_PM2.5 are
 * an upper bound on ventilation, not a measurement of it. They are
 * nevertheless the number you want for "how fast does this room clear
 * particles", which is why they are reported separately rather than averaged.
 *
 * HOW IT IS FITTED. For a FIXED lambda the model is linear in the two
 * remaining parameters, because u = exp(-lambda*t) makes it C ~ a + b*u. So
 * this does an ordinary least-squares solve for (a, b) at each candidate
 * lambda and searches only that one dimension - a log-spaced grid, then
 * golden-section refinement. That is far more robust than a general non-linear
 * solver: there is no initial guess to get wrong and no divergence.
 *
 * WHAT A SHORT WINDOW CAN AND CANNOT SEE. Over a window of length T, an
 * exponential is only distinguishable from a straight line once lambda*T is an
 * appreciable fraction of 1. Five minutes is T = 1/12 h, so a room at
 * eACH = 1 changes by only 8% across the whole window and lambda is weakly
 * determined even when R^2 is excellent. That is physics, not a bug, and
 * hiding it would be worse than reporting it: every result therefore carries
 * `resolution` (= lambda*T) and a profile-likelihood interval, and
 * {@link assessFit} flags a fit the window cannot pin down. Longer windows
 * resolve low eACH properly.
 *
 * RESIDUAL AUTOCORRELATION. Readings arrive about once a second and successive
 * residuals are correlated, which makes any nominal standard error optimistic.
 * Binning (see {@link binSamples}) is applied first partly for that reason -
 * bin means are far less correlated than raw samples - but the interval should
 * still be read as indicative rather than exact. It is also conditional on the
 * model: a window in which the source itself changed is described by no single
 * exponential, and no interval computed from the residuals can know that.
 *
 * WHICH IS WHY THERE IS A STABILITY CHECK. Each fit is repeated on the first
 * and second half of its window, and the two rates compared. One exponential
 * can describe a window containing a source change with an R^2 above 0.95
 * while returning a rate several times the truth - R^2 says the curve fits,
 * not that the model is right. The halves do not agree in that case, and
 * `assessFit` rejects it. On generated data with known rates this cut the
 * share of ten-minute windows reported more than 50% wrong from 57% to 6%,
 * where an R^2 gate alone changed nothing.
 *
 * ACCURACY, measured the same way (three rooms at 2, 6 and 12 air changes per
 * hour, 1% sensor noise, sources switching every ten minutes): five-minute
 * windows that pass every gate have a median error around 3%, and fewer than
 * one in ten is more than 50% out. The 95% interval contained the true rate
 * about 87% of the time - slightly optimistic, as the autocorrelation note
 * above predicts.
 */

/** One reading: `t` in hours from an arbitrary origin, `y` in the data's own unit. */
export type FitPoint = { t: number; y: number };

/** A raw reading before binning. */
export type Sample = { t: number; y: number };

/** The outcome of the one-dimensional search. */
export type ExpFit = {
  /** Rate of approach to the asymptote, per hour. This is eACH. */
  lambda: number;
  /** Fitted steady-state concentration, data units. */
  asymptote: number;
  /**
   * Fitted amplitude at the window start: positive while decaying towards the
   * asymptote, negative while building up to it.
   */
  amplitude: number;
  /** Coefficient of determination, 0..1. */
  r2: number;
  /** Root-mean-square residual, data units. */
  rmse: number;
  /** Points fitted (after binning). */
  n: number;
  /** Window covered by those points, hours. */
  spanHours: number;
  /** Lambda * spanHours — how much of the exponential this window actually sees. */
  resolution: number;
  /**
   * Ratio between the rates fitted to the first and second half of the window,
   * larger over smaller, so 1 means perfect agreement. Null when the halves
   * were too short to fit. See the stability note in the module docstring.
   */
  stability: number | null;
  /** Lower end of the ~95% profile interval on lambda, or null if unbounded. */
  lambdaLow: number | null;
  /** Upper end of the ~95% profile interval on lambda, or null if unbounded. */
  lambdaHigh: number | null;
  /** Model value at the start of the window. */
  startValue: number;
  /** Model value at the end of the window. */
  endValue: number;
};

/**
 * Search bounds on lambda, per hour.
 *
 * The floor is not zero: as lambda -> 0 the model degenerates into a straight
 * line (C ~ (a+b) - b*lambda*t), where a and b individually run away to
 * infinity while fitting the data just as well. Any value this low is reported
 * as unresolved rather than as a measurement.
 */
const LAMBDA_MIN = 0.05;
/** A room clearing faster than this is beyond what a 1 Hz sensor can follow. */
const LAMBDA_MAX = 60;
/** Log-spaced probes across [LAMBDA_MIN, LAMBDA_MAX] before refinement. */
const GRID_STEPS = 160;
/** Golden-section iterations; each cuts the bracket by ~38%. */
const REFINE_STEPS = 40;

/**
 * Least-squares solve of y ~ a + b*exp(-lambda*t) at a fixed lambda.
 * @param points - Readings with `t` already re-based on the window start.
 * @param lambda - The rate to solve at, per hour.
 * @returns The residual sum of squares and the two fitted coefficients, or
 * null when this lambda leaves no spread in exp(-lambda*t) to regress on.
 */
function solveAt(
  points: FitPoint[],
  lambda: number
): { sse: number; a: number; b: number } | null {
  const n = points.length;
  const u = new Array<number>(n);
  let sumU = 0;
  let sumY = 0;
  for (let i = 0; i < n; i += 1) {
    u[i] = Math.exp(-lambda * points[i].t);
    sumU += u[i];
    sumY += points[i].y;
  }
  const meanU = sumU / n;
  const meanY = sumY / n;
  let suu = 0;
  let suy = 0;
  for (let i = 0; i < n; i += 1) {
    const du = u[i] - meanU;
    suu += du * du;
    suy += du * (points[i].y - meanY);
  }
  // No spread left in u - this lambda cannot be distinguished from a constant.
  if (!(suu > 1e-14)) return null;
  const b = suy / suu;
  const a = meanY - b * meanU;
  let sse = 0;
  for (let i = 0; i < n; i += 1) {
    const r = points[i].y - (a + b * u[i]);
    sse += r * r;
  }
  return { sse, a, b };
}

/**
 * Residual sum of squares at a lambda, or Infinity where it cannot be solved.
 * @param points - Readings with `t` re-based on the window start.
 * @param lambda - The rate to evaluate, per hour.
 * @returns The residual sum of squares.
 */
function sseAt(points: FitPoint[], lambda: number): number {
  const got = solveAt(points, lambda);
  return got ? got.sse : Number.POSITIVE_INFINITY;
}

/**
 * Bisects for the lambda where SSE crosses `target`, walking outwards from
 * `from` in the given direction. Returns null when the crossing is outside the
 * search bounds, which means the data does not bound lambda on that side.
 * @param points - Readings with `t` re-based on the window start.
 * @param from - The best-fit lambda to walk outwards from.
 * @param target - The residual sum of squares that marks the interval edge.
 * @param direction - 1 to walk towards larger lambda, -1 towards smaller.
 * @returns The crossing lambda, or null when there is none inside the bounds.
 */
function profileEdge(
  points: FitPoint[],
  from: number,
  target: number,
  direction: 1 | -1
): number | null {
  const limit = direction > 0 ? LAMBDA_MAX : LAMBDA_MIN;
  let inside = from;
  let outside: number | null = null;
  // Walk out in multiplicative steps until SSE exceeds the target.
  let probe = from;
  for (let i = 0; i < 48; i += 1) {
    probe = direction > 0 ? probe * 1.15 : probe / 1.15;
    if (direction > 0 ? probe >= limit : probe <= limit) break;
    if (sseAt(points, probe) > target) {
      outside = probe;
      break;
    }
    inside = probe;
  }
  if (outside === null) return null;
  for (let i = 0; i < 40; i += 1) {
    const mid = Math.sqrt(inside * outside);
    if (sseAt(points, mid) > target) outside = mid;
    else inside = mid;
  }
  return outside;
}

/**
 * Fits C(t) = asymptote + amplitude*exp(-lambda*t) by searching lambda only.
 *
 * Times are re-based on the first point so that exp(-lambda*t) <= 1 throughout,
 * which keeps the normal equations well conditioned however the caller chose
 * its time origin.
 * @param samples - Readings, `t` in hours. At least four, spanning real time.
 * @param options - Fitting options.
 * @param options.stability - False skips the split-half check. Used when
 * fitting the halves themselves, which must not recurse.
 * @returns The fit, or null when the data cannot support one.
 */
export function fitExponential(
  samples: FitPoint[],
  options: { stability?: boolean } = {}
): ExpFit | null {
  if (samples.length < 4) return null;
  const ordered = [...samples].sort((p, q) => p.t - q.t);
  const t0 = ordered[0].t;
  const spanHours = ordered[ordered.length - 1].t - t0;
  if (!(spanHours > 0)) return null;
  const points = ordered.map((p) => ({ t: p.t - t0, y: p.y }));
  const n = points.length;

  let bestLambda = Number.NaN;
  let bestSse = Number.POSITIVE_INFINITY;
  const ratio = LAMBDA_MAX / LAMBDA_MIN;
  for (let i = 0; i < GRID_STEPS; i += 1) {
    const lambda = LAMBDA_MIN * ratio ** (i / (GRID_STEPS - 1));
    const sse = sseAt(points, lambda);
    if (sse < bestSse) {
      bestSse = sse;
      bestLambda = lambda;
    }
  }
  if (!Number.isFinite(bestLambda)) return null;

  // Golden-section refinement in log-lambda, bracketed by the neighbouring
  // grid points. The grid is fine enough that the minimum is unimodal inside
  // one cell.
  const step = ratio ** (1 / (GRID_STEPS - 1));
  let lo = Math.max(LAMBDA_MIN, bestLambda / step);
  let hi = Math.min(LAMBDA_MAX, bestLambda * step);
  const phi = (Math.sqrt(5) - 1) / 2;
  let x1 = Math.exp(Math.log(hi) - phi * (Math.log(hi) - Math.log(lo)));
  let x2 = Math.exp(Math.log(lo) + phi * (Math.log(hi) - Math.log(lo)));
  let f1 = sseAt(points, x1);
  let f2 = sseAt(points, x2);
  for (let i = 0; i < REFINE_STEPS; i += 1) {
    if (f1 < f2) {
      hi = x2;
      x2 = x1;
      f2 = f1;
      x1 = Math.exp(Math.log(hi) - phi * (Math.log(hi) - Math.log(lo)));
      f1 = sseAt(points, x1);
    } else {
      lo = x1;
      x1 = x2;
      f1 = f2;
      x2 = Math.exp(Math.log(lo) + phi * (Math.log(hi) - Math.log(lo)));
      f2 = sseAt(points, x2);
    }
  }
  const lambda = f1 < f2 ? x1 : x2;
  const solved = solveAt(points, lambda);
  if (!solved) return null;

  const meanY = points.reduce((acc, p) => acc + p.y, 0) / n;
  const sst = points.reduce((acc, p) => acc + (p.y - meanY) ** 2, 0);
  // A perfectly flat series has no variance to explain. Report R^2 = 0 rather
  // than dividing by zero: a flat line carries no information about lambda.
  const r2 = sst > 0 ? Math.max(0, 1 - solved.sse / sst) : 0;
  const rmse = Math.sqrt(solved.sse / n);

  // Profile interval on lambda, the one parameter of interest. The residual
  // sum of squares rises by SSE_min * dchi2/dof at the edge, and dchi2 = 4
  // gives roughly 95%.
  //
  // NOT 1 sigma (dchi2 = 1), which was the first thing tried: a 1 sigma
  // interval excludes the true value about a third of the time by
  // construction, and a range printed in brackets next to a number is read as
  // "the answer is in here", not as "68% of the time". Quoting the tighter
  // interval made a correct fit look wrong on test data whose true rate was
  // known. Widening it is a presentation decision, not a change to the fit.
  const dof = n - 3;
  const target = dof > 0 ? solved.sse * (1 + 4 / dof) : Number.POSITIVE_INFINITY;
  const lambdaLow = Number.isFinite(target)
    ? profileEdge(points, lambda, target, -1)
    : null;
  const lambdaHigh = Number.isFinite(target)
    ? profileEdge(points, lambda, target, 1)
    : null;

  // Split-half stability: fit each half of the window on its own and compare.
  // A window in which the source switched part-way through fits ONE
  // exponential with an excellent R^2 and returns a rate several times the
  // truth; the two halves, however, disagree wildly, and that is what catches
  // it. Measured on generated data with known rates, over ten-minute windows
  // this cut the share of reported values that were more than 50% wrong from
  // 57% to 6%. An R^2 gate alone did not move that number at all, and neither
  // did a test for the concentration reversing direction.
  let stability: number | null = null;
  if (options.stability !== false && points.length >= 10) {
    const half = Math.floor(points.length / 2);
    /**
     * Re-bases a half on its own first point, so each is conditioned the same
     * way the whole window is.
     * @param part - One half of the binned window.
     * @returns The same points with `t` starting at zero.
     */
    const rebase = (part: FitPoint[]): FitPoint[] =>
      part.map((q) => ({ t: q.t - part[0].t, y: q.y }));
    const left = fitExponential(rebase(points.slice(0, half)), { stability: false });
    const right = fitExponential(rebase(points.slice(half)), { stability: false });
    if (left && right && left.lambda > 0 && right.lambda > 0) {
      stability =
        Math.max(left.lambda, right.lambda) / Math.min(left.lambda, right.lambda);
    }
  }

  return {
    lambda,
    asymptote: solved.a,
    amplitude: solved.b,
    r2,
    rmse,
    n,
    spanHours,
    resolution: lambda * spanHours,
    stability,
    lambdaLow,
    lambdaHigh,
    startValue: solved.a + solved.b,
    endValue: solved.a + solved.b * Math.exp(-lambda * spanHours)
  };
}

/**
 * Averages raw readings into fixed-width bins, returning bin centres in hours.
 *
 * Two reasons to bin before fitting. It equalises the weight of each stretch of
 * time, so a burst of pings cannot dominate a quiet minute; and bin means are
 * much less autocorrelated than 1 Hz samples, which is what makes the fit's
 * uncertainty estimate meaningful at all. Empty bins are dropped, not
 * interpolated - a gap in collection is not a reading.
 * @param samples - Raw readings, `t` in unix milliseconds.
 * @param fromMs - Window start, unix milliseconds.
 * @param toMs - Window end, unix milliseconds.
 * @param binMs - Bin width in milliseconds.
 * @returns Bin means with `t` in hours measured from `fromMs`.
 */
export function binSamples(
  samples: Sample[],
  fromMs: number,
  toMs: number,
  binMs: number
): FitPoint[] {
  if (binMs <= 0 || !(toMs > fromMs)) return [];
  const count = Math.max(1, Math.ceil((toMs - fromMs) / binMs));
  const sums = new Float64Array(count);
  const hits = new Int32Array(count);
  samples.forEach((s) => {
    if (!Number.isFinite(s.y) || s.t < fromMs || s.t > toMs) return;
    const idx = Math.min(count - 1, Math.floor((s.t - fromMs) / binMs));
    sums[idx] += s.y;
    hits[idx] += 1;
  });
  const out: FitPoint[] = [];
  for (let i = 0; i < count; i += 1) {
    if (hits[i] > 0) {
      out.push({ t: ((i + 0.5) * binMs) / 3600000, y: sums[i] / hits[i] });
    }
  }
  return out;
}

/** Why a fit is not being reported as an eACH value. */
export type FitRejection =
  | 'no-data'
  | 'too-few-points'
  | 'window-too-short'
  | 'poor-fit'
  | 'unresolved'
  | 'unstable'
  | 'out-of-range';

/** Gates applied to a fit before its lambda may be called an eACH. */
export type FitGates = {
  /** Minimum R^2. The request specifies 0.75. */
  minR2: number;
  /** Minimum binned points. */
  minPoints: number;
  /** Minimum fraction of the requested window that must carry data. */
  minCoverage: number;
  /**
   * Minimum lambda*span. Below this the curve is indistinguishable from a
   * straight line and lambda is not actually measured.
   */
  minResolution: number;
  /**
   * How far the rates fitted to the two halves of the window may disagree
   * before the window is rejected, as a ratio. See the stability note in the
   * module docstring.
   */
  maxInstability: number;
};

/** Defaults: the R^2 = 0.75 gate from the request, plus identifiability guards. */
export const DEFAULT_GATES: FitGates = {
  minR2: 0.75,
  minPoints: 8,
  minCoverage: 0.6,
  minResolution: 0.15,
  maxInstability: 3
};

/**
 * Decides whether a fit's lambda may be reported as an eACH.
 * @param fit - The fit, or null when fitting failed.
 * @param requestedSpanHours - Window the caller asked for, hours.
 * @param gates - Thresholds to apply.
 * @returns Null when the fit is reportable, otherwise the reason it is not.
 */
export function assessFit(
  fit: ExpFit | null,
  requestedSpanHours: number,
  gates: FitGates = DEFAULT_GATES
): FitRejection | null {
  if (!fit) return 'no-data';
  if (fit.n < gates.minPoints) return 'too-few-points';
  if (requestedSpanHours > 0 && fit.spanHours < gates.minCoverage * requestedSpanHours) {
    return 'window-too-short';
  }
  if (fit.r2 < gates.minR2) return 'poor-fit';
  if (fit.lambda <= LAMBDA_MIN * 1.02 || fit.lambda >= LAMBDA_MAX * 0.98) return 'out-of-range';
  if (fit.resolution < gates.minResolution) return 'unresolved';
  // Last, because a window too short to resolve ANY rate also fails this one,
  // and "use a longer window" is the more useful thing to be told than
  // "something changed". A window can otherwise fit one exponential
  // beautifully and still be two regimes stitched together.
  if (fit.stability !== null && fit.stability > gates.maxInstability) return 'unstable';
  return null;
}

/** Plain-English explanation of a rejection, for the UI. */
export const REJECTION_TEXT: Readonly<Record<FitRejection, string>> = {
  'no-data': 'No readings in this window.',
  'too-few-points': 'Not enough readings to fit a curve.',
  'window-too-short': 'Readings cover too little of the window.',
  'poor-fit': 'The readings are not following an exponential (R² below the threshold).',
  unstable:
    'The two halves of this window imply very different rates, so something changed during it — a source switching on or off, or a door opening. Narrow the range to one steady stretch.',
  unresolved:
    'The concentration barely changed across the window, so the rate is not measurable from it — use a longer window.',
  'out-of-range': 'The fitted rate is outside the range this method can measure.'
};

/** Volume-normalised and absolute source strength derived from a fit. */
export type EmissionStrength = {
  /**
   * Source strength per unit volume, in the parameter's own unit per hour:
   * lambda * (C_inf - C_background). Negative when the fitted plateau sits
   * below the background, which means a sink rather than a source.
   */
  perVolume: number;
  /** PerVolume * volume, when a room volume was supplied. */
  absolute: number | null;
  /** Unit for `absolute`. */
  absoluteUnit: string | null;
};

/**
 * Source emission strength implied by a fit.
 *
 * At steady state the source exactly balances removal, so S/V = lambda *
 * (C_inf - C_out). That identity is what makes the emission rate a by-product
 * of the same fit rather than a separate measurement - but it inherits the
 * fit's weaknesses, and it is only as good as the background concentration the
 * caller supplies.
 * @param fit - A fit whose lambda has passed the gates.
 * @param background - Outdoor/background concentration, same unit as the data.
 * @param volumeM3 - Room volume in m³, or null to skip the absolute rate.
 * @param toAbsolute - Converts (perVolume, volume) into an absolute labelled rate.
 * @returns The strength, or null when there is no fit.
 */
export function emissionStrength(
  fit: ExpFit | null,
  background: number,
  volumeM3: number | null,
  toAbsolute?: (perVolume: number, volume: number) => { value: number; unit: string }
): EmissionStrength | null {
  if (!fit) return null;
  const perVolume = fit.lambda * (fit.asymptote - background);
  if (volumeM3 === null || !(volumeM3 > 0) || !toAbsolute) {
    return { perVolume, absolute: null, absoluteUnit: null };
  }
  const converted = toAbsolute(perVolume, volumeM3);
  return { perVolume, absolute: converted.value, absoluteUnit: converted.unit };
}

/**
 * CO₂ generation of one adult doing sedentary office work, litres per hour.
 *
 * 0.0052 L/s is the rate ASHRAE 62.1's appendix and Persily's occupancy work
 * use for an adult at ~1.2 met. Dividing a measured CO₂ generation rate by it
 * estimates how many people are in the room - a well-established technique,
 * but one that assumes the activity level, so it is presented as an estimate
 * and never as a count.
 */
export const CO2_PER_PERSON_LPH = 0.0052 * 3600;
